import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthGuard";
import { usePresenceHeartbeat } from "./usePresenceHeartbeat";

type Status = "online" | "busy" | "offline";

/**
 * Keeps a signed-in doctor's presence heartbeat alive across the WHOLE app.
 *
 * Previously usePresenceHeartbeat was mounted only inside DoctorDashboard, so
 * the heartbeat stopped the moment the doctor navigated anywhere else — into a
 * consultation, the health feed, their profile. After 2 minutes of no
 * heartbeat two things then marked them offline:
 *   - the client-side staleness check in Doctors.tsx / DoctorWall.tsx, which
 *     forces effectiveStatus to "offline" when last_online_at is stale, and
 *   - the presence-sweep edge function, which actually WRITES
 *     availability_status = 'offline'.
 *
 * The result was a doctor who saw "online" on their own dashboard while every
 * patient saw them offline — worst of all while they were inside a
 * consultation, i.e. exactly when they were most active.
 *
 * Mounting this once at app level keeps last_online_at fresh on every route.
 */
export function useDoctorPresence() {
  const { userId, hasRole } = useAuth();
  const isDoctor = hasRole("doctor");

  const [doctorId, setDoctorId] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("offline");

  // Resolve the doctor row for the signed-in user.
  useEffect(() => {
    if (!userId || !isDoctor) {
      setDoctorId(null);
      setStatus("offline");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("doctors")
          .select("id, availability_status")
          .eq("user_id", userId)
          .maybeSingle();
        if (cancelled || !data) return;
        setDoctorId(data.id);
        setStatus((data.availability_status as Status | null) ?? "offline");
      } catch {
        /* non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isDoctor]);

  // Track status changes made elsewhere (the dashboard toggle, or the sweep)
  // so the heartbeat starts/stops in step with them.
  useEffect(() => {
    if (!doctorId) return;
    const channel = supabase
      .channel(`presence-self:${doctorId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "doctors", filter: `id=eq.${doctorId}` },
        (payload) => {
          const next = (payload.new as { availability_status?: Status } | null)?.availability_status;
          if (next) setStatus(next);
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [doctorId]);

  // While doctorId is unresolved we pass "offline", which makes the heartbeat
  // a no-op — so this is safe for patients and signed-out visitors.
  usePresenceHeartbeat({
    doctorId: doctorId ?? "",
    currentStatus: doctorId ? status : "offline",
    onStatusChange: setStatus,
  });
}
